import React, { useEffect, useState, useMemo } from "react"
import { OrderEntity } from "../../Objects/Order/OrderEntity"
import { ICartItem } from "../../Interfaces/ICardItem"
import { AddressManager } from "../Components/AddressManager"
import { usePublicViewContext } from "../../context/usePublicViewContext"
import { EOrderPlaceType } from "../../Enum/EOrderPlaceType"
import { OrderCatalogItem } from "../../Objects/Order/OrderCatalogItem"
import { OrderProduct } from "../../Objects/Order/OrderProduct"
import { mapAddtionals, mapSelections } from "../Helpers/BuildProductList"
import { useSaveOrder } from "../../hooks/Order/useSaveOrder"
import { usePayment } from "../../hooks/Payment/usePayment"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { CalculateCartSummary, getItemTotal } from "../Helpers/CalculateCartSummary"
import { AwardAppliedList } from "../Components/AwardAppliedCard"
import { converteDateToSql, setDelay } from "../../Objects/Generics/Helps"
import { OrderPaymentEntity } from "../../Objects/Order/OrderPaymentEntity"
import { companyMemory } from "../../Objects/Memory/Memory"
import { useValidateCoupon } from "../../hooks/Coupon/useValidateCoupon"
import { CouponEntity } from "../../Objects/Coupon/CouponEntity"
import InputField from "../../components/InputField/InputField"
import { IShippingFeeResultDto } from "../../Interfaces/IShippingFeeResultDto"
import ModalPortal from "../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { useRedirectPayment } from "../../context/useRedirectPayment"
import { EDeliveryFulfillmentType } from "../../Enum/EDeliveryFulfillmentType"
import { ERequestType } from "../../Enum/ERequestType"
import GetSpace from "../../utils/space"
import { isPlanLimitError } from "../../utils/planLimits"
import { getPublicMenuTheme, isLegacyPublicMenuTheme } from "../Helpers/PublicMenuTheme"
import { EOrderPaymentProviderType } from "../../Enum/EOrderPaymentProviderType"

interface CheckoutPupopProps {
  items: ICartItem[]
  onClose: () => void
  onSuccess: (order: OrderEntity) => void
  blockedMessage?: string
  onOrderBlocked?: (message: string) => void
  servicePointSelfService?: { servicePointId: number; servicePointName: string; canCreateOrders: boolean }
}

type CheckoutStep = 1 | 2 | 3

type CheckoutPaymentMethod = "presential" | "online"

export const CheckoutPupop: React.FC<CheckoutPupopProps> = ({
  items,
  onClose,
  onSuccess,
  blockedMessage,
  onOrderBlocked,
  servicePointSelfService,
}) => {
  const { storeSlug } = useParams<{ storeSlug: string }>()

  const { t } = useTranslation("general")

  const { auth } = usePublicViewContext()

  const theme = getPublicMenuTheme(companyMemory)

  const isLegacyTheme = isLegacyPublicMenuTheme(theme)

  const [step, setStep] = useState<CheckoutStep>(1)

  const [selectedAddressId, setSelectedAddressId] = useState<number>(0)

  const [isProcessing, setIsProcessing] = useState(false)

  const { isRedirectingToPayment, stopRedirect, startRedirect } = useRedirectPayment()

  const [isCalculatingShipping, setIsCalculatingShipping] = useState(false)

  const [friendlyLimitMessage, setFriendlyLimitMessage] = useState(blockedMessage ?? "")

  const [payment, setPayment] = useState<CheckoutPaymentMethod>("presential")

  const [selectedFulfillmentType, setSelectedFulfillmentType] = useState<"delivery" | "pickup">("delivery")

  const [couponCode, setCouponCode] = useState("")

  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false)

  const [couponApplied, setCouponApplied] = useState<CouponEntity | null>(null)

  const [selfServiceCustomerName, setSelfServiceCustomerName] = useState("")

  const [selfServiceCustomerCpf, setSelfServiceCustomerCpf] = useState("")

  const [pickupAddressCopied, setPickupAddressCopied] = useState(false)

  const [onlineProviders, setOnlineProviders] = useState<
    { providerType: EOrderPaymentProviderType; isOnlinePaymentEnabled: boolean }[]
  >([])

  const [selectedOnlineProvider, setSelectedOnlineProvider] = useState<EOrderPaymentProviderType | null>(null)

  const canPayOnline = onlineProviders.length > 0

  const paymentOptions = useMemo((): { id: CheckoutPaymentMethod; label: string; icon: string }[] => {
    const options: { id: CheckoutPaymentMethod; label: string; icon: string }[] = [
      { id: "presential", label: t("publicMenu:checkout.pay_in_person"), icon: "💵" },
    ]

    if (canPayOnline) {
      options.push({ id: "online", label: t("publicMenu:checkout.pay_online"), icon: "💳" })
    }

    return options
  }, [canPayOnline, t])

  const { handleSavePublicOrder, handleCalculateShippingFee } = useSaveOrder()

  const { createOrderOnlineCheckout, getStorePaymentProviders } = usePayment()

  useEffect(() => {
    void (async () => {
      const allProviders = await getStorePaymentProviders()

      const providers = allProviders.filter((p) => p.isOnlinePaymentEnabled)

      setOnlineProviders(providers)

      if (providers.length === 1) {
        setSelectedOnlineProvider(providers[0].providerType)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { handleValidateCoupon } = useValidateCoupon()

  const [shippingFeeResultDto, setIShippingFeeResultDto] = useState<IShippingFeeResultDto>(null)

  const storeFulfillmentType = companyMemory.deliveryFulfillmentType ?? EDeliveryFulfillmentType.Both

  const canPickupInStore =
    storeFulfillmentType === EDeliveryFulfillmentType.PickupOnly ||
    storeFulfillmentType === EDeliveryFulfillmentType.Both

  const canDelivery =
    storeFulfillmentType === EDeliveryFulfillmentType.DeliveryOnly ||
    storeFulfillmentType === EDeliveryFulfillmentType.Both
  const isStorePickupSelected = canPickupInStore && selectedFulfillmentType === "pickup"

  const pickupAddressLine = companyMemory.addressEntity
    ? `${companyMemory.addressEntity.street}, ${companyMemory.addressEntity.number} - ${companyMemory.addressEntity.neighborhood}, ${companyMemory.addressEntity.city}/${companyMemory.addressEntity.state}`
    : ""

  useEffect(() => {
    if (!canDelivery && canPickupInStore) {
      setSelectedFulfillmentType("pickup")
      return
    }

    if (canDelivery && !canPickupInStore) {
      setSelectedFulfillmentType("delivery")
    }
  }, [canDelivery, canPickupInStore])

  const productsList = useMemo(() => {
    return items
      .filter((item) => item.id.startsWith("product-") || item.id.startsWith("combo-"))
      .flatMap((item) => {
        const productId = item.id.startsWith("combo-")
          ? Number(item.linkedProductId)
          : Number(item.id.replace("product-", ""))

        const addtionals = mapAddtionals(item)

        const selections = mapSelections(item)

        return Array.from({ length: item.quantity }).map(
          () =>
            new OrderProduct({
              productEntityId: productId,
              description: item.notes || "",
              isReservation: item.isReservation ?? false,
              reservationDate: item.reservationDate ?? "",
              reservationTime: item.reservationTime ?? "",
              addtionals,
              selections,
            }),
        )
      })
  }, [items])

  const catalogItemsList = useMemo(() => {
    return items
      .filter((item) => item.id.startsWith("catalogItem-"))
      .flatMap((item) => {
        const catalogItemId = Number(item.id.replace("catalogItem-", ""))

        const addtionals = mapAddtionals(item)

        const selections = mapSelections(item)

        return Array.from({ length: item.quantity }).map(
          () =>
            new OrderCatalogItem({
              catalogItemEntityId: catalogItemId,
              description: item.notes || "",
              isReservation: item.isReservation ?? false,
              reservationDate: item.reservationDate ?? "",
              reservationTime: item.reservationTime ?? "",
              addtionals,
              selections,
            }),
        )
      })
  }, [items])

  const calculateShippingFee = async (addressId?: number) => {
    if (isStorePickupSelected) {
      setIShippingFeeResultDto(null)
      return
    }

    const targetAddressId = addressId ?? selectedAddressId

    if (!targetAddressId) {
      setIShippingFeeResultDto(null)

      return
    }

    try {
      setIsCalculatingShipping(true)

      await handleCalculateShippingFee(addressId, totalWithCoupon, (e: IShippingFeeResultDto) => {
        setIShippingFeeResultDto(e)
      })
    } finally {
      setIsCalculatingShipping(false)
    }
  }

  const summary = useMemo(
    () => CalculateCartSummary(items, servicePointSelfService ? null : auth.customer),
    [items, auth.customer, servicePointSelfService],
  )

  const couponDiscount = couponApplied ? (summary.total * couponApplied.discountPercentage) / 100 : 0

  const totalWithCoupon = Math.max(0, summary.total - couponDiscount)

  const finalTotal =
    totalWithCoupon +
    (step >= 2 && !isStorePickupSelected ? (shippingFeeResultDto ? shippingFeeResultDto.shippingFee : 0) : 0)

  const handleNextStep = () => {
    if (step === 2 && !isStorePickupSelected && !selectedAddressId) {
      return
    }

    setStep((s) => Math.min(3, s + 1) as CheckoutStep)
  }

  const handleApplyCoupon = async () => {
    setIsApplyingCoupon(true)

    await setDelay(100)

    if (!couponCode.trim()) {
      setCouponApplied(null)

      setIsApplyingCoupon(false)

      return
    }

    const coupon = await handleValidateCoupon(couponCode)

    setIsApplyingCoupon(false)

    if (!coupon) {
      setCouponApplied(null)

      return
    }

    setCouponApplied(coupon)
  }

  const handleCopyPickupAddress = async () => {
    if (!pickupAddressLine) {
      return
    }

    try {
      await navigator.clipboard.writeText(pickupAddressLine)

      setPickupAddressCopied(true)

      window.setTimeout(() => setPickupAddressCopied(false), 2200)
    } catch {
      setPickupAddressCopied(false)
    }
  }

  const getPickupWhatsappShareLink = () => {
    if (!pickupAddressLine) {
      return "#"
    }

    const text = encodeURIComponent(
      t("publicMenu:checkout.pickup_address_share_message", {
        store: companyMemory.companyName || "Loja",
        address: pickupAddressLine,
      }),
    )

    return `https://wa.me/?text=${text}`
  }

  const clearPersistedPublicCart = () => {
    const cartStorageKey = `orderpro_public_cart:${storeSlug ?? "default"}:${EOrderPlaceType.Delivery}`

    localStorage.removeItem(cartStorageKey)
  }

  const handleFinish = async () => {
    if (
      friendlyLimitMessage ||
      (servicePointSelfService &&
        (!servicePointSelfService.canCreateOrders ||
          !selfServiceCustomerName.trim() ||
          selfServiceCustomerCpf.replace(/\D/g, "").length !== 11))
    ) {
      return
    }

    setIsProcessing(true)

    try {
      const newOrder = new OrderEntity({
        customerEntity: servicePointSelfService ? null : auth.customer,
        eOrderPlaceType: servicePointSelfService ? EOrderPlaceType.Local : EOrderPlaceType.Delivery,
        isStorePickup: isStorePickupSelected,
        orderCatalogItems: catalogItemsList,
        orderProducts: productsList,
        extraServices: [],
        isCancel: false,
        addressEntityId: isStorePickupSelected ? 0 : selectedAddressId,
        couponCode: couponApplied?.code ?? "",
        orderPaymentEntity: new OrderPaymentEntity({
          date: converteDateToSql(new Date()),
          description:
            payment === "online" && canPayOnline
              ? t("publicMenu:checkout.online_order_description")
              : t("publicMenu:checkout.presential_payment_description"),
          isOnlinePayment: payment === "online" && canPayOnline,
        }),
      })

      if (servicePointSelfService) {
        newOrder.servicePointId = servicePointSelfService.servicePointId
        newOrder.customerId = 0
        newOrder.couponCode = ""
        newOrder.selfServiceCustomerName = selfServiceCustomerName.trim()
        newOrder.selfServiceCustomerCpf = selfServiceCustomerCpf.replace(/\D/g, "")
      }

      await handleSavePublicOrder(
        newOrder,
        API_ROUTES.ORDER.CREATE_PUBLIC(storeSlug),
        async (createdOrderData) => {
          const createdOrder = new OrderEntity(createdOrderData)

          onSuccess(createdOrder)

          if (payment === "online" && canPayOnline && createdOrder.id > 0) {
            startRedirect()

            await setDelay(500)

            const onlineCheckoutResponse = await createOrderOnlineCheckout(
              createdOrder.id,
              selectedOnlineProvider || undefined,
            )

            if (!onlineCheckoutResponse || !onlineCheckoutResponse?.initPoint) {
              throw new Error(t("publicMenu:checkout.online_payment_start_error"))
            }

            clearPersistedPublicCart()

            window.location.href = onlineCheckoutResponse.initPoint

            await setDelay(2000)

            return
          }
        },
        (message) => {
          if (!isPlanLimitError(message)) {
            return
          }

          const customerFriendlyMessage = t("ID18")

          setFriendlyLimitMessage(customerFriendlyMessage)
          onOrderBlocked?.(customerFriendlyMessage)
        },
        servicePointSelfService ? ERequestType.ServicePointSelfService : ERequestType.Customer,
      )
    } finally {
      stopRedirect()

      setIsProcessing(false)
    }
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className="fixed inset-0 flex items-center justify-center p-2 sm:p-4">
            <div
              className="relative w-full max-w-xl h-[calc(100dvh-1rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] max-h-[calc(100dvh-1rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] sm:h-auto sm:max-h-[92dvh] bg-white shadow-2xl flex flex-col rounded-lg overflow-hidden animate-in slide-in-from-bottom-10 duration-500 ring-2 ring-orange-500"
              style={
                isLegacyTheme
                  ? undefined
                  : {
                      backgroundColor: theme.cartBackgroundColor,
                      boxShadow: `0 0 0 2px ${theme.cartBorderColor}`,
                      color: theme.textColor,
                    }
              }
            >
              <div
                className="px-3 sm:px-5 pt-3 sm:pt-4 pb-3 sm:pb-4 shrink-0 border-b border-gray-200"
                style={isLegacyTheme ? undefined : { borderColor: theme.cartBorderColor }}
              >
                {isRedirectingToPayment && (
                  <Animate type={AnimationTypes.fadeInTraslate} duration="1s">
                    <div className="mb-5 rounded-lg border border-blue-300 bg-blue-50 p-4">
                      <p className="text-[12px] font-bold text-blue-800">
                        {t("publicMenu:checkout.redirecting_online_payment_title")}
                      </p>

                      <p className="mt-1 text-[12px] text-blue-700">
                        {t("publicMenu:checkout.redirecting_online_payment_description")}
                      </p>
                    </div>
                  </Animate>
                )}

                {friendlyLimitMessage && (
                  <Animate type={AnimationTypes.fadeInTraslate} duration="1s">
                    <div className="mb-5 rounded-lg border border-amber-300 bg-amber-50 p-4">
                      <p className="text-[12px] font-bold text-amber-800">⚠️ Pedidos temporariamente pausados</p>

                      <p className="mt-1 text-[12px] text-amber-700">{friendlyLimitMessage}</p>
                    </div>
                  </Animate>
                )}

                <div className="flex justify-between items-center mb-2 sm:mb-4">
                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
                    <div className="min-w-0">
                      <h2
                        className="text-base sm:text-xl font-black text-gray-900 uppercase tracking-tight truncate"
                        style={isLegacyTheme ? undefined : { color: theme.cartTitleTextColor }}
                      >
                        {t("publicMenu:checkout.title")}
                      </h2>
                    </div>
                  </Animate>

                  {!isProcessing && !isRedirectingToPayment && (
                    <Animate type={AnimationTypes.slideRightIn} duration="1.2s">
                      <button
                        onClick={onClose}
                        className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-gray-50 rounded-lg ring-2 ring-red-500 text-gray-400 hover:text-gray-900 transition-colors"
                        data-interaction-description-key="interactionDescriptions:publicMenu.pupop.checkoutPupop.button.onClose"
                      >
                        <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2.5"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </Animate>
                  )}
                </div>

                <Animate type={AnimationTypes.slideRightIn} duration="1.4s">
                  <div className="flex items-center justify-center gap-2">
                    {(servicePointSelfService ? [1] : [1, 2, 3]).map((s) => (
                      <div
                        key={s}
                        className={`h-1 rounded-full transition-all duration-500 ${step === s ? "w-8 sm:w-10 bg-gray-950" : s < step ? "w-3 sm:w-4 bg-orange-600" : "w-3 sm:w-4 bg-gray-100"}`}
                        style={
                          isLegacyTheme
                            ? undefined
                            : { backgroundColor: s <= step ? theme.cartButtonColor : theme.categoryBackgroundColor }
                        }
                      />
                    ))}
                  </div>
                </Animate>
              </div>

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.8s">
                <div
                  className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-5 no-scrollbar py-3 sm:py-4"
                  style={isLegacyTheme ? undefined : { backgroundColor: theme.cartBackgroundColor }}
                >
                  {step === 1 && (
                    <div className="space-y-6 animate-in fade-in duration-300">
                      {servicePointSelfService && (
                        <div className="display-flex flex-col mb-6">
                          <div className="sm:col-span-2 mb-4">
                            <p className="text-xs font-black uppercase tracking-wider text-orange-800">
                              {t("servicePointSelfService:customer_identification")}
                            </p>

                            <p className="mt-1 text-xs font-medium text-slate-600">
                              {t("servicePointSelfService:customer_identification_hint")}
                            </p>
                          </div>

                          <InputField
                            type="text"
                            value={selfServiceCustomerName}
                            onChange={(event) => setSelfServiceCustomerName(event.target.value)}
                            placeholder={t("servicePointSelfService:customer_name")}
                            required
                            maxLength={120}
                            autoComplete="name"
                            descriptionKey="interactionDescriptions:publicMenu.pupop.checkoutPupop.input.customerName"
                          />

                          <GetSpace top={10} />

                          <InputField
                            type="text"
                            value={selfServiceCustomerCpf}
                            onChange={(event) =>
                              setSelfServiceCustomerCpf(event.target.value.replace(/\D/g, "").slice(0, 11))
                            }
                            placeholder={t("servicePointSelfService:customer_cpf")}
                            required
                            maxLength={11}
                            inputMode="numeric"
                            autoComplete="off"
                            descriptionKey="interactionDescriptions:publicMenu.pupop.checkoutPupop.input.customerCpf"
                          />
                        </div>
                      )}

                      <span
                        className="text-[12px] font-black text-gray-500 uppercase tracking-widest"
                        style={isLegacyTheme ? undefined : { color: theme.cartTotalLabelTextColor }}
                      >
                        {t("publicMenu:checkout.items_summary")}
                      </span>

                      {!servicePointSelfService && <AwardAppliedList summary={summary} />}

                      <div className="space-y-4">
                        {items.map((item) => {
                          return (
                            <div key={item.uniqueId} className="flex items-center gap-4 py-1">
                              <img
                                src={item.image.toClientRequest()}
                                className="w-12 h-12 rounded-xl bg-white object-fit ring-1 ring-gray-100 shadow-sm"
                              />

                              <div className="flex-1 min-w-0">
                                <h4
                                  className="font-bold text-xs text-gray-950 uppercase tracking-tight truncate"
                                  style={isLegacyTheme ? undefined : { color: theme.textColor }}
                                >
                                  {item.name}
                                </h4>

                                <p
                                  className="text-[12px] text-gray-400 font-medium"
                                  style={isLegacyTheme ? undefined : { color: theme.textColor, opacity: 0.7 }}
                                >
                                  {item.quantity}x • R$ {item.price.toFixed(2)}
                                </p>

                                {item.selectedAddOns.length > 0 && (
                                  <ul>
                                    {item.selectedAddOns.map((addOn) => (
                                      <li key={addOn.id} className="text-[10px] font-bold text-red-400 truncate">
                                        + {addOn.quantity}x {addOn.name} (R$ {(addOn.price * addOn.quantity).toFixed(2)}
                                        )
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>

                              <span
                                className="font-black text-xs text-gray-950"
                                style={isLegacyTheme ? undefined : { color: theme.cartTotalValueTextColor }}
                              >
                                R$ {getItemTotal(item).toFixed(2)}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-4">
                      {canPickupInStore && canDelivery && (
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => setSelectedFulfillmentType("delivery")}
                            className={`rounded-lg border-2 px-3 py-2 text-[11px] font-black uppercase tracking-wide ${!isStorePickupSelected ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 text-gray-600"}`}
                            style={
                              isLegacyTheme || isStorePickupSelected
                                ? undefined
                                : {
                                    borderColor: theme.primaryColor,
                                    backgroundColor: theme.secondaryColor,
                                    color: theme.accentColor,
                                  }
                            }
                            data-interaction-description-key="interactionDescriptions:publicMenu.pupop.checkoutPupop.button.setSelectedFulfillmentTypeDelivery"
                          >
                            🛵 {t("publicMenu:checkout.fulfillment_delivery")}
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              setSelectedFulfillmentType("pickup")
                              setSelectedAddressId(0)
                              setIShippingFeeResultDto(null)
                            }}
                            className={`rounded-lg border-2 px-3 py-2 text-[11px] font-black uppercase tracking-wide ${isStorePickupSelected ? "border-green-500 bg-green-50 text-green-700" : "border-gray-200 text-gray-600"}`}
                            style={
                              isLegacyTheme || !isStorePickupSelected
                                ? undefined
                                : {
                                    borderColor: theme.primaryColor,
                                    backgroundColor: theme.secondaryColor,
                                    color: theme.accentColor,
                                  }
                            }
                            data-interaction-description-key="interactionDescriptions:publicMenu.pupop.checkoutPupop.button.fulfillmentPickup"
                          >
                            🏪 {t("publicMenu:checkout.fulfillment_pickup")}
                          </button>
                        </div>
                      )}

                      {!isStorePickupSelected && (
                        <AddressManager
                          title={t("publicMenu:address_list.my_destinations")}
                          emptyLabel={t("publicMenu:checkout.add_destination")}
                          selectedAddressId={selectedAddressId}
                          onAddressesChanged={({ type, addressId }) => {
                            const shouldResetSelectedAddress =
                              type === "delete"
                                ? selectedAddressId === addressId
                                : type === "update" && selectedAddressId === addressId

                            if (!shouldResetSelectedAddress) {
                              return
                            }

                            setSelectedAddressId(0)
                            setIShippingFeeResultDto(null)
                          }}
                          onClick={(address) => {
                            setSelectedAddressId(address.id)
                            calculateShippingFee(address.id)
                          }}
                        />
                      )}

                      {isStorePickupSelected && (
                        <div className="space-y-3">
                          <div className="rounded-lg border-2 border-green-300 bg-green-50 px-3 py-2 text-[12px] text-green-800">
                            {t("publicMenu:checkout.pickup_selected_hint")}
                          </div>

                          <div className="rounded-lg border-2 border-emerald-300 bg-white p-3">
                            <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
                              {t("publicMenu:checkout.pickup_address_title")}
                            </p>
                            <p className="mt-1 text-[12px] font-semibold text-gray-700">
                              {pickupAddressLine || t("publicMenu:checkout.pickup_address_missing")}
                            </p>

                            <div className="mt-3 flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={handleCopyPickupAddress}
                                disabled={!pickupAddressLine}
                                className="rounded-lg border-2 border-emerald-300 px-3 py-1.5 text-[10px] font-black uppercase tracking-wide text-emerald-700 disabled:opacity-50"
                                data-interaction-description-key="interactionDescriptions:publicMenu.pupop.checkoutPupop.button.handleCopyPickupAddress"
                              >
                                {pickupAddressCopied
                                  ? t("publicMenu:checkout.pickup_address_copied")
                                  : t("publicMenu:checkout.pickup_copy_address")}
                              </button>

                              <a
                                href={getPickupWhatsappShareLink()}
                                target="_blank"
                                rel="noreferrer"
                                className={`rounded-lg border-2 border-emerald-300 px-3 py-1.5 text-[10px] font-black uppercase tracking-wide text-emerald-700 ${!pickupAddressLine ? "pointer-events-none opacity-50" : ""}`}
                                data-interaction-description-key="interactionDescriptions:publicMenu.pupop.checkoutPupop.link.pickupShareWhatsapp"
                              >
                                {t("publicMenu:checkout.pickup_share_whatsapp")}
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {step === 3 && (
                    <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
                      <div className="space-y-2 animate-in fade-in duration-300">
                        <span className="text-[12px] font-black text-gray-500 uppercase tracking-widest">
                          {t("publicMenu:checkout.payment_method")}
                        </span>

                        <div className="grid gap-3">
                          {paymentOptions.map((opt) => (
                            <button
                              key={opt.id}
                              onClick={() => setPayment(opt.id)}
                              className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${payment === opt.id ? "border-gray-950 bg-white ring-2 ring-orange-500" : "border-gray-50 bg-gray-50/50"}`}
                              style={
                                isLegacyTheme
                                  ? undefined
                                  : {
                                      borderColor: payment === opt.id ? theme.primaryColor : theme.borderColor,
                                      backgroundColor: theme.surfaceColor,
                                      color: theme.textColor,
                                      boxShadow: payment === opt.id ? `0 0 0 2px ${theme.primaryColor}` : undefined,
                                    }
                              }
                              data-interaction-description-key="interactionDescriptions:publicMenu.pupop.checkoutPupop.button.setPaymentOptIdAsAny"
                            >
                              <div className="flex items-center gap-4">
                                <span className="text-xl">{opt.icon}</span>

                                <span className="font-black text-xs uppercase tracking-tight">{opt.label}</span>
                              </div>

                              <div
                                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${payment === opt.id ? "border-orange-600 bg-orange-600" : "border-gray-200"}`}
                                style={
                                  isLegacyTheme || payment !== opt.id
                                    ? undefined
                                    : { borderColor: theme.primaryColor, backgroundColor: theme.primaryColor }
                                }
                              >
                                {payment === opt.id && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                              </div>
                            </button>
                          ))}
                        </div>

                        {payment === "online" && onlineProviders.length > 1 && (
                          <div className="mt-4 space-y-2">
                            <span className="text-[11px] font-black text-gray-500 uppercase tracking-widest">
                              {t("publicMenu:checkout.online_provider_choose")}
                            </span>

                            <div className="grid gap-2">
                              {onlineProviders.map((provider) => (
                                <button
                                  key={provider.providerType}
                                  type="button"
                                  onClick={() => setSelectedOnlineProvider(provider.providerType)}
                                  className={`flex items-center justify-between rounded-lg border-2 px-3 py-2 text-xs font-bold ${selectedOnlineProvider === provider.providerType ? "border-gray-950 bg-white ring-2 ring-orange-500" : "border-gray-50 bg-gray-50/50"}`}
                                  data-interaction-description-key="interactionDescriptions:publicMenu.pupop.checkoutPupop.button.selectOnlineProvider"
                                >
                                  {provider.providerType.toOrderPaymentProviderTypeString()}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </Animate>
                  )}
                </div>
              </Animate>

              <div
                className="px-3 sm:px-5 pt-3 sm:pt-5 pb-[calc(0.875rem+env(safe-area-inset-bottom))] sm:pb-[calc(1.25rem+env(safe-area-inset-bottom))] bg-white border-t border-gray-200 shrink-0"
                style={
                  isLegacyTheme
                    ? undefined
                    : { backgroundColor: theme.cartBackgroundColor, borderColor: theme.cartBorderColor }
                }
              >
                <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <div className="flex flex-col gap-2 mb-3 sm:mb-5 px-1 sm:px-2">
                    {!servicePointSelfService && (
                      <div className="w-full flex gap-2 items-center py-1 mb-2 sm:mb-5">
                        <div className="flex-1 min-w-0">
                          <InputField
                            type="text"
                            value={couponCode}
                            onChange={(e) => {
                              setCouponCode(e.target.value.toUpperCase())
                              setCouponApplied(null)
                            }}
                            placeholder={t("publicMenu:checkout.discount_coupon_placeholder")}
                            descriptionKey="interactionDescriptions:publicMenu.pupop.checkoutPupop.input.discountCouponPlaceholder"
                          />
                        </div>

                        <Animate type={AnimationTypes.fadeInTraslate}>
                          <button
                            type="button"
                            onClick={handleApplyCoupon}
                            disabled={isApplyingCoupon}
                            className="shrink-0 w-[86px] sm:w-[100px] h-9 sm:h-10 px-2 sm:px-4 flex items-center justify-center rounded-lg ring-2 ring-red-500 text-gray-600 text-[9px] sm:text-[10px] font-black uppercase disabled:opacity-50"
                            data-interaction-description-key="interactionDescriptions:publicMenu.pupop.checkoutPupop.button.handleApplyCoupon"
                          >
                            {isApplyingCoupon ? (
                              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              t("publicMenu:checkout.apply")
                            )}
                          </button>
                        </Animate>
                      </div>
                    )}

                    {couponApplied && (
                      <div className="text-[10px] font-bold text-green-600 uppercase tracking-widest">
                        {t("publicMenu:checkout.coupon_applied", {
                          code: couponApplied.code,
                          discount: couponApplied.discountPercentage,
                        })}
                      </div>
                    )}

                    {!servicePointSelfService && step !== 2 && (
                      <div className="flex justify-between text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                        <span>{t("publicMenu:checkout.gross_total")}</span>

                        <span>R$ {summary.grossTotal.toFixed(2)}</span>
                      </div>
                    )}

                    <div className="flex justify-between text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                      <span>{t("publicMenu:checkout.subtotal")}</span>

                      <span>R$ {summary.subtotal.toFixed(2)}</span>
                    </div>

                    {step !== 2 && (
                      <div className="flex justify-between text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                        <span>{t("publicMenu:checkout.promotion_discount")}</span>

                        <span>- R$ {summary.savings?.toFixed(2) || "0.00"}</span>
                      </div>
                    )}

                    {step !== 2 && (
                      <div className="flex justify-between text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                        <span>{t("publicMenu:checkout.award_discount")}</span>

                        <span>- R$ {summary.totalDiscount.toFixed(2)}</span>
                      </div>
                    )}

                    {couponApplied && (
                      <div className="flex justify-between text-[10px] font-bold text-green-600 uppercase tracking-widest">
                        <span>{t("publicMenu:checkout.coupon")}</span>

                        <span>- R$ {couponDiscount.toFixed(2)}</span>
                      </div>
                    )}

                    {step >= 2 && !isStorePickupSelected && shippingFeeResultDto && (
                      <>
                        <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest">
                          <div className="flex items-center gap-2">
                            <span className="text-orange-600">{t("publicMenu:checkout.delivery_fee")}</span>

                            {isCalculatingShipping ? (
                              <div className="w-2 h-2 rounded-full bg-orange-200 animate-ping" />
                            ) : (
                              <span className="text-[10px] font-medium text-gray-500">
                                ({shippingFeeResultDto.distanceKm} km)
                              </span>
                            )}
                          </div>

                          <span className="text-orange-600 font-black">
                            {shippingFeeResultDto.isFreeShippingApplied
                              ? t("publicMenu:checkout.free")
                              : `+ R$ ${shippingFeeResultDto.shippingFee.toFixed(2)}`}
                          </span>
                        </div>
                      </>
                    )}

                    <div className="flex items-end justify-between flex-col pt-1">
                      <span
                        className="text-[9px] font-black text-gray-900 uppercase tracking-widest mb-1"
                        style={isLegacyTheme ? undefined : { color: theme.cartTotalLabelTextColor }}
                      >
                        {t("publicMenu:checkout.grand_total")}
                      </span>

                      <div
                        className={`text-xl sm:text-2xl font-black text-gray-950 tracking-tighter transition-opacity ${isCalculatingShipping ? "opacity-30" : "opacity-100"}`}
                        style={isLegacyTheme ? undefined : { color: theme.cartTotalValueTextColor }}
                      >
                        R$ {finalTotal.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </Animate>

                <div className="flex items-center justify-between flex-col">
                  <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
                    {!servicePointSelfService && step > 1 && !isProcessing && (
                      <button
                        onClick={() => setStep((s) => Math.max(1, s - 1) as CheckoutStep)}
                        className="h-9 sm:h-12 px-4 sm:px-6 rounded-full text-gray-600 font-black text-[10px] uppercase tracking-widest hover:text-gray-950 transition-all"
                        data-interaction-description-key="interactionDescriptions:publicMenu.pupop.checkoutPupop.button.setStepSS1AsAny"
                      >
                        {t("publicMenu:checkout.back")}
                      </button>
                    )}

                    <Animate type={AnimationTypes.slideRightIn} duration="2s">
                      <button
                        disabled={
                          !!friendlyLimitMessage ||
                          isProcessing ||
                          isCalculatingShipping ||
                          (step === 2 && !isStorePickupSelected && selectedAddressId == 0) ||
                          (step === 3 &&
                            payment === "online" &&
                            onlineProviders.length > 1 &&
                            !selectedOnlineProvider) ||
                          (servicePointSelfService &&
                            (!servicePointSelfService.canCreateOrders ||
                              !selfServiceCustomerName.trim() ||
                              selfServiceCustomerCpf.length !== 11))
                        }
                        onClick={servicePointSelfService || step === 3 ? handleFinish : handleNextStep}
                        className={`h-10 sm:h-12 px-6 sm:px-10 rounded-lg font-black text-[10px] sm:text-[12px] uppercase tracking-[0.16em] sm:tracking-[0.2em] shadow-2xl active:scale-95 transition-all flex items-center gap-2 sm:gap-3 disabled:opacity-30 disabled:grayscale ${step === 3 ? "bg-red-600 hover:bg-red-600 text-white shadow-gray-200" : "bg-orange-600 text-white shadow-orange-100"}`}
                        style={
                          isLegacyTheme
                            ? undefined
                            : { backgroundColor: theme.cartButtonColor, color: theme.buttonTextColor }
                        }
                        data-interaction-description-key={
                          servicePointSelfService || step === 3
                            ? "interactionDescriptions:publicMenu.pupop.checkoutPupop.button.finishOrder"
                            : "interactionDescriptions:publicMenu.pupop.checkoutPupop.button.continueCheckout"
                        }
                      >
                        {isProcessing ? (
                          <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-lg animate-spin" />
                        ) : servicePointSelfService || step === 3 ? (
                          t("publicMenu:checkout.finish_order")
                        ) : (
                          t("publicMenu:checkout.continue")
                        )}
                      </button>
                    </Animate>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
